const React = require('react');
const { useEffect } = require('react');
const { useRef } = require('react');
const { useState } = require('react');
const { default: Styled } = require('styled-components');
const UniqueId = require('lodash/uniqueId');
const IsEqual = require('lodash/isEqual');

const { default: Typography } = require('@mui/material/Typography');

const BottomButtonContainer = require('components/BottomButtonContainer');
const PreventNavigationDialog = require('components/PreventNavigationDialog');
const { analyticsTemplates } = require('utils/analytics');

const InterestsInputField = require('containers/InterestsInputField');
const { INTERESTS } = require('utils/constants');
const T = require('prop-types');
const LoadingScreen = require('components/LoadingScreen');

const internals = {};

internals.shouldHydrateFromSignupDetails = ({
    currentInterests,
    currentPassionInterests,
    pageIsBlocking,
    signupDetailsLoaded,
    signupDetailsInterests,
    signupDetailsPassionInterests
} = {}) => {

    if (!signupDetailsLoaded || pageIsBlocking) {
        return false;
    }

    return !IsEqual(signupDetailsInterests, currentInterests)
        || !IsEqual(signupDetailsPassionInterests, currentPassionInterests);
};

const EditInterests = (props) => {

    const { Wrapper, FieldsWrapper } = internals;
    const { history, signupDetails = {}, interestsLoading } = props;

    const fields = ['interests', 'passionInterests'];
    const signupDetailsLoaded = Array.isArray(signupDetails.interests);
    const signupDetailsInterests = signupDetailsLoaded ? signupDetails.interests : [];
    const signupDetailsPassionInterests = Array.isArray(signupDetails.passionInterests)
        ? signupDetails.passionInterests
        : [];

    const uniqueId = UniqueId('user-inputs-');

    const [pageState, setPageState] = useState({
        interestsErrorShow: false,
        interests: signupDetailsInterests.slice(),
        passionInterests: signupDetailsPassionInterests.slice()
    });
    const pageIsBlockingRef = useRef(false);

    const [pageHasErrors, setPageHasErrors] = useState(false);
    const [pageIsBlocking, setPageIsBlocking] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [successRedirectPath, setSuccessRedirectPath] = useState('/app/welcome');

    useEffect(() => {

        if (typeof props.onLoad === 'function') {
            props.onLoad();
        }
    }, []);

    useEffect(() => {

        if (props.location && props.location.state) {
            if (props.location.state.returnTo) {
                setSuccessRedirectPath(props.location.state.returnTo);
            }
        }
    }, []);

    useEffect(() => {

        if (!internals.shouldHydrateFromSignupDetails({
            currentInterests: pageState.interests,
            currentPassionInterests: pageState.passionInterests,
            pageIsBlocking: pageIsBlockingRef.current,
            signupDetailsLoaded,
            signupDetailsInterests,
            signupDetailsPassionInterests
        })) {
            return;
        }

        setPageState((prevState) => ({
            ...prevState,
            interests: signupDetailsInterests,
            passionInterests: signupDetailsPassionInterests,
            interestsErrorShow: false
        }));
    }, [
        interestsLoading,
        pageState.interests,
        pageState.passionInterests,
        signupDetails.interests,
        signupDetails.passionInterests,
        signupDetailsLoaded
    ]);

    const onInterestsChange = (ev, value) => {

        if (value === null) {
            value = [];
        }

        setPageState((prevState) => ({
            ...prevState,
            interests: value
        }));

        pageIsBlockingRef.current = true;
        setPageIsBlocking(true);
    };

    const onPassionInterestsChange = (value) => {

        if (value === null) {
            value = [];
        }

        setPageState((prevState) => ({
            ...prevState,
            passionInterests: value
        }));

        pageIsBlockingRef.current = true;
        setPageIsBlocking(true);
    };

    const getSignupDetails = () => {

        const details = fields.reduce((collector, field) => {

            let fieldValue = pageState[field];

            // Force the values to be null instead of emptystring
            if (fieldValue === '') {
                fieldValue = null;
            }

            collector[field] = fieldValue;

            return collector;
        }, {});

        return {
            ...details
        };
    };

    const validate = (field) => {

        const value = pageState[field];

        switch (field) {
            case 'interests':

                if (!value.length) {
                    return 'Start typing to find and select at least one Interest';
                }

                if (value.length > INTERESTS.LIMIT) {
                    return `Sorry, you have exceeded the maximum number of ${INTERESTS.LIMIT} Interests. Please remove some before adding new Interests`;
                }

                break;
        }

        return null;
    };

    const submit = () => {

        const hasErrors = fields.some(validate);

        if (hasErrors) {

            setPageHasErrors(true);

            // On submit attempt, all errors are fair game to display
            fields.forEach((field) => {

                let fieldErrorShow = false;
                if (validate(field)){
                    fieldErrorShow = true;
                }

                setPageState((prevState) => ({
                    ...prevState,
                    [`${field}ErrorShow`]: fieldErrorShow
                }));
            });

            return props.onInvalidSubmit();
        }

        setSubmitting(true);
        setPageHasErrors(false);
        pageIsBlockingRef.current = false;
        setPageIsBlocking(false);

        props.onSubmit(getSignupDetails(), successRedirectPath).then(() => {

            setSubmitting(false);
            setPageHasErrors(false);
            pageIsBlockingRef.current = false;
        setPageIsBlocking(false);
        });
    };

    const submitNoRedirect = () => {

        const hasErrors = fields.some(validate);

        if (hasErrors) {

            setPageHasErrors(true);
            // On submit attempt, all errors are fair game to display
            fields.forEach((field) => {

                let fieldErrorShow = false;
                if (validate(field)){
                    fieldErrorShow = true;
                }

                setPageState((prevState) => ({
                    ...prevState,
                    [`${field}ErrorShow`]: fieldErrorShow
                }));
            });

            return props.onInvalidSubmit();
        }

        // No errors?  Submit the field/values

        setSubmitting(true);
        setPageHasErrors(false);
        pageIsBlockingRef.current = false;
        setPageIsBlocking(false);

        props.onSubmitNoRedirect(getSignupDetails()).then(() => {

            setSubmitting(false);
            setPageHasErrors(false);
            pageIsBlockingRef.current = false;
        setPageIsBlocking(false);
        });
    };

    const onBottomButtonClick = (ev) => {

        analyticsTemplates.buttons('save my interests', 'my interests: save my interests request');
        submit(ev);
    };

    const onBottomButtonClickNoRedirect = (ev) => {

        analyticsTemplates.buttons('save my interests', 'my interests: save my interests request');
        submitNoRedirect(ev);
    };

    const sortInterests = (a, b) => {

        const aSpecial = a && a.special;
        const bSpecial = b && b.special;
        const aCategoryName = a?.categoryName || a?.category?.name || '';
        const bCategoryName = b?.categoryName || b?.category?.name || '';
        const aName = a?.name || '';
        const bName = b?.name || '';

        return (Number(aSpecial) - Number(bSpecial )) || aCategoryName.localeCompare(bCategoryName) ||  aName.localeCompare(bName);
    };

    const error = (field) => {

        const fieldError = validate(field);

        if (fieldError !== null) {

            return <div id={uniqueId + '-' + field + '-error-message'} role='alert' aria-live='assertive'>{fieldError}</div>;
        }

        return '';
    };

    const fieldHasError = (field) => {

        if (validate(field) !== null) {

            return true;
        }

        return false;
    };

    const showError = (field, show = fieldHasError(field)) => {

        if (show) {
            setPageHasErrors(true);
        }

        const fieldName = `${field}ErrorShow`;

        setPageState((prevState) => ({
            ...prevState,
            [fieldName]: !!show
        }));
    };

    const renderInterestsField = () => {

        const { interests, interestsErrorShow, passionInterests } = pageState;
        const showInterestsError = !!interestsErrorShow && fieldHasError('interests');

        return <InterestsInputField
            values={!interestsLoading ? interests.slice().sort(sortInterests) : []}
            passionValues={!interestsLoading ? passionInterests.slice().sort(sortInterests) : []}
            onChange={onInterestsChange}
            onChangePassion={onPassionInterestsChange}
            onBlur={() => showError('interests')}
            showError={showInterestsError}
            errorText={showInterestsError ? error('interests') : ''}
            isSignUpPage={false}
            isSearch={false}
            disableClearable
        />;
    };

    // eslint-disable-next-line no-extra-boolean-cast
    if (!!interestsLoading || !signupDetailsLoaded) {
        return <LoadingScreen variant='content' />;
    }

    return (
        <Wrapper>
            <BottomButtonContainer
                btnLabel='Save'
                onBtnClick={onBottomButtonClick}
                disabled={submitting}
            >
                <PreventNavigationDialog
                    // When should shouldBlockNavigation be invoked,
                    // simply passing a boolean
                    // (same as "when" prop of Prompt of React-Router)
                    saveFunction={onBottomButtonClickNoRedirect}
                    alertMsg={'You have made changes.\n Do you want to save or discard them?'}
                    when={pageIsBlocking}
                    // Navigate function
                    navigate={(path,state) => history.push(path,state)}
                    // Use as "message" prop of Prompt of React-Router
                    shouldBlockNavigation={(location) => {

                        if (pageIsBlocking || pageHasErrors) {
                            return true;
                        }

                        return false;
                    }}
                    pageHasErrors={pageHasErrors}
                />
                <FieldsWrapper>
                    <Typography
                        variant='h2'
                        style={{ marginBottom: 10 }}
                    >
                        Edit Interests & Passions
                    </Typography>
                    {renderInterestsField()}
                </FieldsWrapper>
            </BottomButtonContainer>
        </Wrapper>
    );
};

EditInterests.propTypes = {
    onLoad: T.func,
    onSubmit: T.func.isRequired,
    onSubmitNoRedirect:T.func.isRequired,
    onInvalidSubmit: T.func.isRequired,
    interestsLoading: T.bool,
    signupDetails: T.shape({
        interests: T.array,
        passionInterests: T.array
    }),
    history: T.object.isRequired,
    location : T.object
};

internals.Wrapper = Styled.div`
    overflow: hidden;
    height: 100%;
`;

internals.FieldsWrapper = Styled.div`
    padding: 1.5em;
    margin-top: 1rem;
`;

module.exports = EditInterests;
